import {
    Button,
    DataGrid,
    DataGridBody,
    DataGridCell,
    DataGridHeader,
    DataGridHeaderCell,
    DataGridRow,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Dropdown,
    Field,
    Input,
    Option,
    TableCellLayout,
    type TableColumnDefinition,
    createTableColumn,
    makeStyles,
    tokens,
} from '@fluentui/react-components';
import { AddRegular, DeleteRegular } from '@fluentui/react-icons';
import { useCallback, useMemo, useState } from 'react';
import { z } from 'zod';

type AffectedPerson = {
    role: 'Mieter' | 'Eigentümer' | 'Sonstiger';
    name: string | null;
    phone: string | null;
    email: string | null;
    address: string | null;
    buildingUnit: string | null;
    householdInsuranceInsurer: string | null;
    householdInsuranceContractNumber: string | null;
};

type AffectedPersonsTableProps = {
    persons: AffectedPerson[];
    onChange: (persons: AffectedPerson[]) => void;
    status: 'draft' | 'filing' | 'filed' | 'failed';
};

const useStyles = makeStyles({
    dialogBackdrop: {
        backgroundColor: 'transparent',
    },
});

export function AffectedPersonsTable({ persons, onChange, status }: AffectedPersonsTableProps) {
    const styles = useStyles();
    const [editingPerson, setEditingPerson] = useState<AffectedPerson | null>(null);
    const [editingIndex, setEditingIndex] = useState<number | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [validationErrors, setValidationErrors] = useState<{ name?: string; email?: string }>({});

    const columns: TableColumnDefinition<AffectedPerson>[] = useMemo(
        () => [
            createTableColumn<AffectedPerson>({
                columnId: 'role',
                compare: (a, b) => a.role.localeCompare(b.role),
                renderHeaderCell: () => 'Rolle',
                renderCell: (item) => <TableCellLayout>{item.role}</TableCellLayout>,
            }),
            createTableColumn<AffectedPerson>({
                columnId: 'name',
                compare: (a, b) => (a.name ?? '').localeCompare(b.name ?? ''),
                renderHeaderCell: () => 'Name',
                renderCell: (item) => <TableCellLayout>{item.name}</TableCellLayout>,
            }),
            createTableColumn<AffectedPerson>({
                columnId: 'phone',
                compare: (a, b) => (a.phone ?? '').localeCompare(b.phone ?? ''),
                renderHeaderCell: () => 'Telefon',
                renderCell: (item) => <TableCellLayout>{item.phone}</TableCellLayout>,
            }),
            createTableColumn<AffectedPerson>({
                columnId: 'email',
                compare: (a, b) => (a.email ?? '').localeCompare(b.email ?? ''),
                renderHeaderCell: () => 'E-Mail',
                renderCell: (item) => <TableCellLayout>{item.email}</TableCellLayout>,
            }),
            createTableColumn<AffectedPerson>({
                columnId: 'address',
                compare: (a, b) => (a.address ?? '').localeCompare(b.address ?? ''),
                renderHeaderCell: () => 'Adresse',
                renderCell: (item) => <TableCellLayout>{item.address}</TableCellLayout>,
            }),
            createTableColumn<AffectedPerson>({
                columnId: 'buildingUnit',
                compare: (a, b) => (a.buildingUnit ?? '').localeCompare(b.buildingUnit ?? ''),
                renderHeaderCell: () => 'Wohnung',
                renderCell: (item) => <TableCellLayout>{item.buildingUnit}</TableCellLayout>,
            }),
            createTableColumn<AffectedPerson>({
                columnId: 'householdInsurance',
                compare: (a, b) => (a.householdInsuranceInsurer ?? '').localeCompare(b.householdInsuranceInsurer ?? ''),
                renderHeaderCell: () => 'Hausrat',
                renderCell: (item) => <TableCellLayout>{item.householdInsuranceInsurer}</TableCellLayout>,
            }),
            createTableColumn<AffectedPerson>({
                columnId: 'householdInsuranceContract',
                compare: (a, b) =>
                    (a.householdInsuranceContractNumber ?? '').localeCompare(b.householdInsuranceContractNumber ?? ''),
                renderHeaderCell: () => 'Vertrag',
                renderCell: (item) => <TableCellLayout>{item.householdInsuranceContractNumber}</TableCellLayout>,
            }),
        ],
        []
    );

    const handleAddPerson = useCallback(() => {
        setEditingPerson({
            role: 'Mieter',
            name: null,
            phone: null,
            email: null,
            address: null,
            buildingUnit: null,
            householdInsuranceInsurer: null,
            householdInsuranceContractNumber: null,
        });
        setEditingIndex(null);
        setValidationErrors({});
        setIsDialogOpen(true);
    }, []);

    const handleEditPerson = useCallback((person: AffectedPerson, index: number) => {
        setEditingPerson({ ...person });
        setEditingIndex(index);
        setValidationErrors({});
        setIsDialogOpen(true);
    }, []);

    const handleSavePerson = useCallback(() => {
        if (!editingPerson) {
            return;
        }

        // Validate
        const errors: { name?: string; email?: string } = {};

        if (!editingPerson.name || editingPerson.name.trim() === '') {
            errors.name = 'Name ist erforderlich';
        }

        if (editingPerson.email) {
            const emailValidation = z.string().email().safeParse(editingPerson.email);
            if (!emailValidation.success) {
                errors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein';
            }
        }

        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        // Save if valid
        if (editingIndex !== null) {
            const updated = [...persons];
            updated[editingIndex] = editingPerson;
            onChange(updated);
        } else {
            onChange([...persons, editingPerson]);
        }

        setIsDialogOpen(false);
        setEditingPerson(null);
        setEditingIndex(null);
        setValidationErrors({});
    }, [editingPerson, editingIndex, persons, onChange]);

    const handleDeletePerson = useCallback(() => {
        if (editingIndex === null) {
            return;
        }

        onChange(persons.filter((_, i) => i !== editingIndex));
        setIsDialogOpen(false);
        setEditingPerson(null);
        setEditingIndex(null);
    }, [editingIndex, persons, onChange]);

    return (
        <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, minWidth: 0 }}>
                {persons.length !== 0 ? (
                    <div
                        style={{
                            border: `1px solid ${tokens.colorNeutralStroke1}`,
                            borderRadius: tokens.borderRadiusMedium,
                            overflow: 'auto',
                            minWidth: 0,
                        }}
                    >
                        <DataGrid
                            items={persons}
                            columns={columns}
                            sortable
                            getRowId={(item) => String(persons.indexOf(item))}
                        >
                            <DataGridHeader>
                                <DataGridRow style={{ minHeight: '44px' }}>
                                    {({ renderHeaderCell }) => (
                                        <DataGridHeaderCell style={{ fontWeight: tokens.fontWeightSemibold }}>
                                            {renderHeaderCell()}
                                        </DataGridHeaderCell>
                                    )}
                                </DataGridRow>
                            </DataGridHeader>
                            <DataGridBody<AffectedPerson>>
                                {({ item, rowId }) => (
                                    <DataGridRow<AffectedPerson>
                                        key={rowId}
                                        onClick={() => handleEditPerson(item, Number(rowId))}
                                        style={{
                                            cursor: 'pointer',
                                            borderBottom: Number(rowId) === persons.length - 1 ? 'none' : undefined,
                                        }}
                                    >
                                        {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
                                    </DataGridRow>
                                )}
                            </DataGridBody>
                        </DataGrid>
                    </div>
                ) : null}
                <Button
                    appearance={persons.length === 0 ? 'secondary' : 'subtle'}
                    icon={<AddRegular />}
                    onClick={handleAddPerson}
                    disabled={status !== 'draft'}
                >
                    Neue Person
                </Button>
            </div>

            <Dialog open={isDialogOpen} onOpenChange={(_, data) => setIsDialogOpen(data.open)}>
                <DialogSurface backdrop={{ className: styles.dialogBackdrop }}>
                    <DialogBody>
                        <DialogTitle>{editingIndex !== null ? 'Person bearbeiten' : 'Neue Person'}</DialogTitle>
                        <DialogContent>
                            {editingPerson && (
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: tokens.spacingVerticalL,
                                        minWidth: 0,
                                    }}
                                >
                                    <Field label="Rolle" required>
                                        <Dropdown
                                            value={editingPerson.role}
                                            selectedOptions={[editingPerson.role]}
                                            onOptionSelect={(_, data) =>
                                                setEditingPerson({
                                                    ...editingPerson,
                                                    role: data.optionValue as 'Mieter' | 'Eigentümer' | 'Sonstiger',
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        >
                                            <Option value="Mieter">Mieter</Option>
                                            <Option value="Eigentümer">Eigentümer</Option>
                                            <Option value="Sonstiger">Sonstiger</Option>
                                        </Dropdown>
                                    </Field>
                                    <Field
                                        label="Name"
                                        required
                                        validationState={validationErrors.name ? 'error' : undefined}
                                        validationMessage={validationErrors.name}
                                    >
                                        <Input
                                            value={editingPerson.name ?? ''}
                                            onChange={(_, data) =>
                                                setEditingPerson({
                                                    ...editingPerson,
                                                    name: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field label="Telefon">
                                        <Input
                                            value={editingPerson.phone ?? ''}
                                            onChange={(_, data) =>
                                                setEditingPerson({
                                                    ...editingPerson,
                                                    phone: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field
                                        label="E-Mail"
                                        validationState={validationErrors.email ? 'error' : undefined}
                                        validationMessage={validationErrors.email}
                                    >
                                        <Input
                                            type="email"
                                            value={editingPerson.email ?? ''}
                                            onChange={(_, data) =>
                                                setEditingPerson({
                                                    ...editingPerson,
                                                    email: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field label="Adresse">
                                        <Input
                                            value={editingPerson.address ?? ''}
                                            onChange={(_, data) =>
                                                setEditingPerson({
                                                    ...editingPerson,
                                                    address: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field label="Wohnung">
                                        <Input
                                            value={editingPerson.buildingUnit ?? ''}
                                            onChange={(_, data) =>
                                                setEditingPerson({
                                                    ...editingPerson,
                                                    buildingUnit: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field label="Name des Versicherers der Hausratsversicherung">
                                        <Input
                                            value={editingPerson.householdInsuranceInsurer ?? ''}
                                            onChange={(_, data) =>
                                                setEditingPerson({
                                                    ...editingPerson,
                                                    householdInsuranceInsurer: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                    <Field label="Vertragsnummer zur Hausratsversicherung">
                                        <Input
                                            value={editingPerson.householdInsuranceContractNumber ?? ''}
                                            onChange={(_, data) =>
                                                setEditingPerson({
                                                    ...editingPerson,
                                                    householdInsuranceContractNumber: data.value || null,
                                                })
                                            }
                                            style={{ minWidth: 0 }}
                                        />
                                    </Field>
                                </div>
                            )}
                        </DialogContent>
                        <DialogActions>
                            {editingIndex !== null && (
                                <Button appearance="secondary" icon={<DeleteRegular />} onClick={handleDeletePerson}>
                                    Löschen
                                </Button>
                            )}
                            <DialogTrigger disableButtonEnhancement>
                                <Button appearance="secondary">Abbrechen</Button>
                            </DialogTrigger>
                            <Button appearance="primary" onClick={handleSavePerson}>
                                Übernehmen
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
}
